import type {
  IGoogleDriveAdapter,
  IBackupRepository,
  IBackupRemoteAdapter,
  IBackupQuotaAdapter,
  GoogleDriveFile,
} from '@app/domain/ports';
import { BackupSyncState, LocalBackup, ValidationError } from '@app/domain/entities';
import { BackupAuthError } from './errors';

/**
 * Backup remote adapter — pure TypeScript, framework-agnostic.
 *
 * Orchestrates Google Drive file operations with local repository state.
 * The backup folder path is supplied via constructor so this class has
 * zero dependency on app-layer environment config or translation services.
 */
export class BackupRemoteAdapter implements IBackupRemoteAdapter {
  private readonly TIMESTAMP_MATCH_TOLERANCE_MS = 3000;

  constructor(
    private readonly storage: IGoogleDriveAdapter,
    private readonly backupRepository: IBackupRepository,
    private readonly quotaAdapter: IBackupQuotaAdapter,
    /** Remote folder path in Google Drive, or a factory that returns it lazily. */
    private readonly backupPathOrFn: string | (() => string),
  ) {}

  private get backupPath(): string {
    return typeof this.backupPathOrFn === 'function' ? this.backupPathOrFn() : this.backupPathOrFn;
  }

  async ensureAuthorized(opts?: { silent?: boolean; customState?: Record<string, unknown> }): Promise<void> {
    const authorized = await this.storage.authorize({
      ...(opts?.customState !== undefined ? { customState: opts.customState } : {}),
      ...(opts?.silent !== undefined ? { silent: opts.silent } : {}),
    });
    if (!authorized) throw new BackupAuthError();
  }

  async uploadPending(): Promise<{ uploaded: number; failed: number }> {
    return this.uploadPendingBackups();
  }

  async syncMetadata(): Promise<void> {
    return this.syncRemoteMetadata();
  }

  async uploadFile(fileName: string, data: Uint8Array): Promise<string> {
    return this.storage.upload(this.backupPath, fileName, data);
  }

  async downloadFile(driveFileId: string): Promise<Uint8Array> {
    return this.storage.download(driveFileId);
  }

  async deleteFile(driveFileId: string): Promise<void> {
    return this.storage.delete(driveFileId);
  }

  // ───── High-level orchestration methods ─────────────────────────────────────

  async syncWithRemote(opts: { redirect?: string } = {}): Promise<{ uploaded: number; failed: number }> {
    await this.ensureAuthorized({
      ...(opts.redirect ? { customState: { redirect: opts.redirect } } : {}),
    });

    const uploadResult = await this.uploadPendingBackups();
    await this.syncRemoteMetadata();

    console.log('✓ Synchronized with Google Drive');
    return uploadResult;
  }

  async download(backupId: string, driveFileId: string, opts: { redirect?: string } = {}): Promise<Uint8Array> {
    await this.ensureAuthorized({
      ...(opts.redirect ? { customState: { redirect: opts.redirect } } : {}),
    });

    const data = await this.storage.download(driveFileId);
    console.log('✓ Downloaded backup from storage');

    const quotaCheck = await this.quotaAdapter.canStore(data.length);
    if (!quotaCheck.canStore) {
      throw new ValidationError(
        'storageQuota',
        quotaCheck.message ?? 'Quota exceeded — not enough local storage for this backup.',
      );
    }

    await this.backupRepository.saveData(backupId, data);
    await this.backupRepository.update(backupId, { syncState: BackupSyncState.SYNCED });

    return data;
  }

  async upload(backupId: string, opts: { redirect?: string; silent?: boolean } = {}): Promise<string> {
    await this.ensureAuthorized({
      ...(opts.redirect ? { customState: { redirect: opts.redirect } } : {}),
      ...(opts.silent !== undefined ? { silent: opts.silent } : {}),
    });

    try {
      await this.backupRepository.updateSyncState(backupId, BackupSyncState.SYNC_PENDING);

      const entity = await this.backupRepository.getById(backupId);
      if (!entity) throw new ValidationError('backupId', `Backup not found: ${backupId}`);
      const data = await this.backupRepository.findDataById(backupId);
      if (!data) throw new ValidationError('backupData', `Backup data not available locally: ${backupId}`);

      const driveFileId = await this.storage.upload(this.backupPath, entity.fileName, data);
      await this.backupRepository.updateSyncState(backupId, BackupSyncState.SYNCED, driveFileId);

      console.log('\u2713 Synced backup:', backupId);
      return driveFileId;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await this.backupRepository.updateSyncState(backupId, BackupSyncState.SYNC_FAILED, undefined, errorMessage);
      throw error;
    }
  }

  async delete(driveFileId: string): Promise<void> {
    await this.ensureAuthorized({ customState: { redirect: '/backups' } });
    await this.storage.delete(driveFileId);
  }

  async updateRemotePin(driveFileId: string, isPinned: boolean): Promise<void> {
    await this.storage.updateFileProperties(driveFileId, { isPinned: String(isPinned) });
  }

  // ───── Private helpers ───────────────────────────────────────────────────────

  private async uploadPendingBackups(): Promise<{ uploaded: number; failed: number }> {
    const pendingBackups = await this.backupRepository.findPendingBackups();
    let uploadedCount = 0;
    let failedCount = 0;

    for (const backup of pendingBackups) {
      try {
        const data = await this.backupRepository.findDataById(backup.id);
        if (!data) throw new ValidationError('backupData', 'Backup data not available locally');

        await this.backupRepository.updateSyncState(backup.id, BackupSyncState.SYNC_PENDING);
        const driveFileId = await this.storage.upload(this.backupPath, backup.fileName, data);
        await this.backupRepository.updateSyncState(backup.id, BackupSyncState.SYNCED, driveFileId);
        uploadedCount++;
        console.log('✓ Uploaded backup:', backup.fileName);
      } catch (error) {
        console.warn(
          '[BackupRemoteAdapter] Upload failed for backup (non-fatal — continuing with remaining backups):',
          backup.fileName,
          error,
        );
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        await this.backupRepository.updateSyncState(backup.id, BackupSyncState.SYNC_FAILED, undefined, errorMessage);
        failedCount++;
      }
    }

    return { uploaded: uploadedCount, failed: failedCount };
  }

  private async syncRemoteMetadata(): Promise<void> {
    const remoteBackups = await this.storage.list(this.backupPath);
    const localBackups = await this.backupRepository.findAll();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const localByDriveId = new Map(localBackups.filter((b) => b.driveFileId).map((b) => [b.driveFileId!, b]));
    const localByTimestamp = new Map(localBackups.map((b) => [b.createdAt, b]));
    const remoteIds = new Set(remoteBackups.map((r) => r.id));

    for (const remote of remoteBackups) {
      await this.processRemoteBackup(remote, localByDriveId, localByTimestamp);
    }

    await this.handleDeletedRemoteBackups(localBackups, remoteIds);
  }

  private async processRemoteBackup(
    remote: GoogleDriveFile,
    localByDriveId: Map<string, LocalBackup>,
    localByTimestamp: Map<number, LocalBackup>,
  ): Promise<void> {
    const remoteTimestamp = new Date(remote.createdTime).getTime();
    const remotePin = remote.appProperties?.isPinned === 'true';
    let local = localByDriveId.get(remote.id);

    if (!local) {
      local = this.findLocalBackupByTimestamp(remoteTimestamp, localByTimestamp);
    }

    if (!local) {
      await this.createRemoteOnlyMetadata(remote, remoteTimestamp, remotePin);
    } else if (!local.driveFileId) {
      await this.updateLocalBackupWithDriveId(local, remote.id);
      if (local.isPinned !== remotePin) {
        await this.backupRepository.updatePin(local.id, remotePin);
      }
    } else if (local.isPinned !== remotePin) {
      await this.backupRepository.updatePin(local.id, remotePin);
    }
  }

  private findLocalBackupByTimestamp(
    targetTimestamp: number,
    localByTimestamp: Map<number, LocalBackup>,
  ): LocalBackup | undefined {
    for (const [timestamp, backup] of localByTimestamp) {
      if (Math.abs(timestamp - targetTimestamp) <= this.TIMESTAMP_MATCH_TOLERANCE_MS) {
        return backup;
      }
    }
    return undefined;
  }

  private async createRemoteOnlyMetadata(
    remote: { id: string; name: string; size: number },
    timestamp: number,
    isPinned: boolean,
  ): Promise<void> {
    const newBackup: LocalBackup = {
      id: remote.id,
      createdAt: timestamp,
      size: remote.size,
      syncState: BackupSyncState.REMOTE_ONLY,
      driveFileId: remote.id,
      isPinned,
      hasLocalData: false,
      fileName: remote.name,
    };

    await this.backupRepository.create(newBackup);
    console.log('✓ Added remote backup metadata:', remote.name);
  }

  private async updateLocalBackupWithDriveId(local: LocalBackup, driveFileId: string): Promise<void> {
    await this.backupRepository.updateSyncState(local.id, local.syncState, driveFileId);
    console.log('✓ Updated backup with driveFileId:', local.fileName);
  }

  private async handleDeletedRemoteBackups(localBackups: LocalBackup[], remoteIds: Set<string>): Promise<void> {
    for (const local of localBackups) {
      if (!local.driveFileId || remoteIds.has(local.driveFileId)) {
        continue;
      }

      if (local.hasLocalData) {
        // Case 1: Local data exists - clear driveFileId and mark as local only.
        // Note: cast required because exactOptionalPropertyTypes disallows explicit undefined,
        // but the repository's PATCH operation must clear the optional driveFileId field.
        await this.backupRepository.update(local.id, {
          driveFileId: undefined,
          syncState: BackupSyncState.LOCAL_ONLY,
        } as unknown as Partial<Omit<LocalBackup, 'id'>>);
        console.log('✓ Cleared driveFileId for locally available backup:', local.fileName);
      } else {
        await this.backupRepository.delete(local.id);
        console.log('✓ Removed metadata for deleted remote backup:', local.fileName);
      }
    }
  }
}
